import { ENEMY_TYPES, COLORS } from '../config';

export class StickFigureGraphics {
  static bakeTextures(scene: Phaser.Scene): void {
    const gfx = scene.add.graphics();
    const w = 128, h = 128; // Large enough for big weapons
    const cx = 64, cy = 110; // Anchor point near bottom center

    // Helper to generate a texture
    const bake = (key: string, drawFn: () => void) => {
      if (scene.textures.exists(key)) return;
      gfx.clear();
      drawFn();
      gfx.generateTexture(key, w, h);
    };

    // Number of animation frames for walking/attacking loops
    const frames = 8;
    const pi2 = Math.PI * 2;

    // --- ENEMIES ---
    const enemyTypes = Object.keys(ENEMY_TYPES) as (keyof typeof ENEMY_TYPES)[];
    const colors = { normal: COLORS.enemy, hit: COLORS.enemyHit };

    for (const [state, color] of Object.entries(colors)) {
      const stateSuffix = state === 'hit' ? '_hit' : '';

      // Standard walk cycles
      for (const t of enemyTypes) {
        for (let f = 0; f < frames; f++) {
          const phase = (f / frames) * pi2;
          const key = `enemy_${t}_walk${stateSuffix}_${f}`;
          bake(key, () => {
            if (t === 'brute') StickFigureGraphics.drawBrute(gfx, cx, cy, color, phase, false);
            else if (t === 'runner') StickFigureGraphics.drawRunner(gfx, cx, cy, color, phase, false);
            else if (t === 'shaman') StickFigureGraphics.drawShaman(gfx, cx, cy, color, phase, false);
            else if (t === 'archer') StickFigureGraphics.drawEnemyArcher(gfx, cx, cy, color, phase, false);
            else if (t === 'longbow_archer') StickFigureGraphics.drawEnemyLongbow(gfx, cx, cy, color, phase, false);
            else if (t === 'shieldbearer') StickFigureGraphics.drawShieldbearer(gfx, cx, cy, color, phase, false);
            else if (t === 'swarmling') StickFigureGraphics.drawSwarmling(gfx, cx, cy, color, phase, false);
            else if (t === 'splitter') StickFigureGraphics.drawSplitter(gfx, cx, cy, color, phase, false);
            else if (t === 'nest_carrier') StickFigureGraphics.drawNestCarrier(gfx, cx, cy, color, phase, false);
            else if (t === 'enemy_trebuchet') StickFigureGraphics.drawEnemyTrebuchet(gfx, cx, cy, color, phase, false);
            else if (t === 'boulder') StickFigureGraphics.drawBoulder(gfx, cx, cy, color, phase, false);
            else if (t === 'saboteur') StickFigureGraphics.drawSaboteur(gfx, cx, cy, color, phase, false);
            else if (t === 'war_drummer') StickFigureGraphics.drawWarDrummer(gfx, cx, cy, color, phase, false);
            else if (t === 'loot_goblin') StickFigureGraphics.drawLootGoblin(gfx, cx, cy, color, phase, false);
            else if (t === 'siege_tower') StickFigureGraphics.drawSiegeTower(gfx, cx, cy, color, phase, false);
            else StickFigureGraphics.drawEnemy(gfx, cx, cy, color, phase, false);
          });
        }

        // Attack frames (just 1 static pose for simplicity, or 2)
        const atkKey = `enemy_${t}_attack${stateSuffix}`;
        bake(atkKey, () => {
          if (t === 'brute') StickFigureGraphics.drawBrute(gfx, cx, cy, color, 0, true);
          else if (t === 'runner') StickFigureGraphics.drawRunner(gfx, cx, cy, color, 0, true);
          else if (t === 'shaman') StickFigureGraphics.drawShaman(gfx, cx, cy, color, 0, true);
          else if (t === 'archer') StickFigureGraphics.drawEnemyArcher(gfx, cx, cy, color, 0, true);
          else if (t === 'longbow_archer') StickFigureGraphics.drawEnemyLongbow(gfx, cx, cy, color, 0, true);
          else if (t === 'shieldbearer') StickFigureGraphics.drawShieldbearer(gfx, cx, cy, color, 0, true);
          else if (t === 'swarmling') StickFigureGraphics.drawSwarmling(gfx, cx, cy, color, 0, true);
          else if (t === 'splitter') StickFigureGraphics.drawSplitter(gfx, cx, cy, color, 0, true);
          else if (t === 'nest_carrier') StickFigureGraphics.drawNestCarrier(gfx, cx, cy, color, 0, true);
          else if (t === 'enemy_trebuchet') StickFigureGraphics.drawEnemyTrebuchet(gfx, cx, cy, color, 0, true);
          else if (t === 'boulder') StickFigureGraphics.drawBoulder(gfx, cx, cy, color, 0, true);
          else if (t === 'saboteur') StickFigureGraphics.drawSaboteur(gfx, cx, cy, color, 0, true);
          else if (t === 'war_drummer') StickFigureGraphics.drawWarDrummer(gfx, cx, cy, color, 0, true);
          else if (t === 'loot_goblin') StickFigureGraphics.drawLootGoblin(gfx, cx, cy, color, 0, true);
          else if (t === 'siege_tower') StickFigureGraphics.drawSiegeTower(gfx, cx, cy, color, 0, true);
          else StickFigureGraphics.drawEnemy(gfx, cx, cy, color, 0, true);
        });
      }
    }

    // --- FRIENDLIES (Level 1 base models) ---
    // Rock Thrower
    for (let f = 0; f < frames; f++) {
      const phase = (f / frames) * 1.5; // Throw phase ranges roughly 0 to 1
      bake(`friendly_rock_${f}`, () => StickFigureGraphics.drawRockThrower(gfx, cx, cy, phase, 1));
    }
    bake(`friendly_rock_idle`, () => StickFigureGraphics.drawRockThrower(gfx, cx, cy, 0, 1));

    // Spear Thrower
    for (let f = 0; f < frames; f++) {
      const phase = (f / frames) * 1.5;
      bake(`friendly_spear_${f}`, () => StickFigureGraphics.drawSpearThrower(gfx, cx, cy, phase, 1));
    }
    bake(`friendly_spear_idle`, () => StickFigureGraphics.drawSpearThrower(gfx, cx, cy, 0, 1));

    // Sling Thrower
    for (let f = 0; f < frames; f++) {
      const phase = (f / frames);
      bake(`friendly_sling_${f}`, () => StickFigureGraphics.drawSlingThrower(gfx, cx, cy, phase, 1));
    }
    bake(`friendly_sling_idle`, () => StickFigureGraphics.drawSlingThrower(gfx, cx, cy, 0, 1));

    // Archer
    for (let f = 0; f < frames; f++) {
      const phase = (f / frames);
      bake(`friendly_archer_${f}`, () => StickFigureGraphics.drawArcher(gfx, cx, cy, phase, 1));
    }
    bake(`friendly_archer_idle`, () => StickFigureGraphics.drawArcher(gfx, cx, cy, 0, 1));

    // Longbow Archer (reusing Enemy method but with friendly green color 0x2d6b2d)
    for (let f = 0; f < frames; f++) {
      const phase = (f / frames);
      bake(`friendly_longbow_archer_${f}`, () => StickFigureGraphics.drawEnemyLongbow(gfx, cx, cy, 0x000000, phase, false));
    }
    bake(`friendly_longbow_archer_idle`, () => StickFigureGraphics.drawEnemyLongbow(gfx, cx, cy, 0x000000, 0, false));

    // Club Swinger
    for (let f = 0; f < frames; f++) {
      const phase = (f / frames) * pi2;
      bake(`friendly_club_${f}`, () => StickFigureGraphics.drawClubSwinger(gfx, cx, cy, phase, 1));
    }
    bake(`friendly_club_idle`, () => StickFigureGraphics.drawClubSwinger(gfx, cx, cy, 0, 1));

    // Buildings
    bake('building_crow', () => StickFigureGraphics.drawCrowTrainer(gfx, cx, cy, 1));
    bake('building_trebuchet', () => StickFigureGraphics.drawTrebuchet(gfx, cx, cy, 1));
    bake('building_beehive', () => StickFigureGraphics.drawBeehiveThrower(gfx, cx, cy, 1));
    bake('building_crossbow', () => StickFigureGraphics.drawCrossbowStation(gfx, cx, cy, 1));
    bake('building_firefly', () => StickFigureGraphics.drawFireflyNest(gfx, cx, cy, 1));
    bake('building_druid', () => StickFigureGraphics.drawDruid(gfx, cx, cy, 1));
    bake('building_wizard', () => StickFigureGraphics.drawLightningWizard(gfx, cx, cy, 1));

    // Boomerang Thrower (wall slot)
    for (let f = 0; f < frames; f++) {
      const phase = (f / frames) * 1.5;
      bake(`friendly_boomerang_${f}`, () => StickFigureGraphics.drawBoomerangThrower(gfx, cx, cy, phase, 1));
    }
    bake(`friendly_boomerang_idle`, () => StickFigureGraphics.drawBoomerangThrower(gfx, cx, cy, 0, 1));

    gfx.destroy();
  }

  static drawStickFigure(
    gfx: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    color: number,
    scale: number = 1,
    armAngle: number = 0,
    legOffset: number = 0,
    lineWidth: number = 2
  ): void {
    gfx.lineStyle(lineWidth, color, 1);

    const headR = 5 * scale;
    const bodyLen = 16 * scale;
    const armLen = 10 * scale;
    const legLen = 12 * scale;

    const headY = y - (bodyLen + headR * 2);
    const neckY = y - bodyLen;
    const shoulderY = neckY + 4 * scale;
    const hipY = y;

    // Head
    gfx.strokeCircle(x, headY, headR);

    // Body
    gfx.beginPath();
    gfx.moveTo(x, neckY);
    gfx.lineTo(x, hipY);
    gfx.strokePath();

    // Arms
    const armBaseAngle = Math.PI / 4;
    const leftArmAngle = armBaseAngle + armAngle;
    const rightArmAngle = armBaseAngle - armAngle;

    gfx.beginPath();
    gfx.moveTo(
      x - Math.cos(leftArmAngle) * armLen,
      shoulderY + Math.sin(leftArmAngle) * armLen
    );
    gfx.lineTo(x, shoulderY);
    gfx.lineTo(
      x + Math.cos(rightArmAngle) * armLen,
      shoulderY + Math.sin(rightArmAngle) * armLen
    );
    gfx.strokePath();

    // Legs
    const legSpread = 6 * scale;
    gfx.beginPath();
    gfx.moveTo(x - legSpread + legOffset, hipY + legLen);
    gfx.lineTo(x, hipY);
    gfx.lineTo(x + legSpread - legOffset, hipY + legLen);
    gfx.strokePath();
  }

  static drawEnemy(
    gfx: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    color: number,
    walkPhase: number,
    isAttacking: boolean,
    scale: number = 1
  ): void {
    const legOffset = isAttacking ? 0 : Math.sin(walkPhase) * 4 * scale;
    const armAngle = isAttacking ? -0.5 : Math.sin(walkPhase * 0.8) * 0.3;
    StickFigureGraphics.drawStickFigure(gfx, x, y, color, scale, armAngle, legOffset);

    const shoulderY = y - 16 * scale + 4 * scale;

    // Club weapon (carried at all times)
    const clubAngle = isAttacking ? -Math.PI / 3 : Math.PI / 5;
    const clubLen = 14 * scale;
    gfx.lineStyle(3, 0x654321, 1);
    gfx.beginPath();
    gfx.moveTo(x - 8 * scale, shoulderY);
    gfx.lineTo(x - 8 * scale + Math.cos(clubAngle) * clubLen, shoulderY + Math.sin(clubAngle) * clubLen);
    gfx.strokePath();
    // Club head
    gfx.fillStyle(0x5a3d2b, 1);
    gfx.fillCircle(x - 8 * scale + Math.cos(clubAngle) * clubLen, shoulderY + Math.sin(clubAngle) * clubLen, 4 * scale);
  }

  // ---- ENEMY VARIANTS ----

  static drawBrute(
    gfx: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    color: number,
    walkPhase: number,
    isAttacking: boolean
  ): void {
    const scale = 1.3;
    const legOffset = isAttacking ? 0 : Math.sin(walkPhase) * 5 * scale;
    const armAngle = isAttacking ? -0.6 : Math.sin(walkPhase * 0.6) * 0.2;
    StickFigureGraphics.drawStickFigure(gfx, x, y, color, scale, armAngle, legOffset, 3);

    const shoulderY = y - 16 * scale + 4 * scale;

    // BIG CLUB weapon
    const clubAngle = isAttacking ? -Math.PI / 2.5 : Math.PI / 6;
    const clubLen = 22 * scale;
    gfx.lineStyle(4, 0x4a3520, 1);
    gfx.beginPath();
    gfx.moveTo(x - 10 * scale, shoulderY);
    gfx.lineTo(x - 10 * scale + Math.cos(clubAngle) * clubLen, shoulderY + Math.sin(clubAngle) * clubLen);
    gfx.strokePath();
    // Big club head (thick bulge)
    const clubEndX = x - 10 * scale + Math.cos(clubAngle) * clubLen;
    const clubEndY = shoulderY + Math.sin(clubAngle) * clubLen;
    gfx.fillStyle(0x5a3d2b, 1);
    gfx.fillCircle(clubEndX, clubEndY, 8);
    gfx.lineStyle(1.5, 0x3a2515, 1);
    gfx.strokeCircle(clubEndX, clubEndY, 8);
    // Wood grain on club head
    gfx.lineStyle(1, 0x4a3520, 0.5);
    gfx.beginPath();
    gfx.arc(clubEndX, clubEndY, 5, 0, Math.PI * 0.7);
    gfx.strokePath();
  }

  static drawRunner(
    gfx: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    color: number,
    walkPhase: number,
    isAttacking: boolean
  ): void {
    const scale = 0.85;
    // Fast leg movement
    const legOffset = isAttacking ? 0 : Math.sin(walkPhase * 1.5) * 6 * scale;
    // Leaning forward when running
    const armAngle = isAttacking ? -0.4 : Math.sin(walkPhase * 1.2) * 0.5;
    StickFigureGraphics.drawStickFigure(gfx, x, y, color, scale, armAngle, legOffset, 1.5);

    const shoulderY = y - 16 * scale + 4 * scale;

    // Sling weapon
    const slingPhase = isAttacking ? walkPhase * 3 : walkPhase;
    const slingRadius = 8;
    gfx.lineStyle(1.5, 0x8b7355, 1);
    if (isAttacking) {
      // Spinning sling
      const spinAngle = slingPhase * Math.PI * 2;
      const stoneX = x + Math.cos(spinAngle) * slingRadius;
      const stoneY = shoulderY + Math.sin(spinAngle) * slingRadius;
      gfx.beginPath();
      gfx.moveTo(x + 5 * scale, shoulderY);
      gfx.lineTo(stoneX, stoneY);
      gfx.strokePath();
      gfx.fillStyle(0x666666, 1);
      gfx.fillCircle(stoneX, stoneY, 2.5);
    } else {
      // Sling at side
      gfx.beginPath();
      gfx.moveTo(x + 5 * scale, shoulderY);
      gfx.lineTo(x + 10 * scale, shoulderY + 6 * scale);
      gfx.strokePath();
      gfx.fillStyle(0x666666, 1);
      gfx.fillCircle(x + 10 * scale, shoulderY + 8 * scale, 2);
    }
  }

  static drawShaman(
    gfx: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    color: number,
    walkPhase: number,
    isAttacking: boolean
  ): void {
    const scale = 1.0;
    const legOffset = isAttacking ? 0 : Math.sin(walkPhase) * 3 * scale;
    const armAngle = Math.sin(walkPhase * 0.5) * 0.2;

    // Shaman has a distinct purple tint
    StickFigureGraphics.drawStickFigure(gfx, x, y, 0x553366, scale, armAngle, legOffset);

    const headY = y - (16 + 10);
    const shoulderY = y - 16 + 4;
    const neckY = y - 16;

    // Bone necklace around neck
    gfx.lineStyle(1.5, 0xeeddcc, 1);
    gfx.beginPath();
    gfx.arc(x, neckY + 2, 6, Math.PI * 0.2, Math.PI * 0.8);
    gfx.strokePath();
    // Bone pieces on necklace
    gfx.fillStyle(0xeeddcc, 1);
    for (let i = 0; i < 5; i++) {
      const angle = Math.PI * 0.2 + (Math.PI * 0.6 / 4) * i;
      const bx = x + Math.cos(angle) * 6;
      const by = neckY + 2 + Math.sin(angle) * 6;
      gfx.fillRect(bx - 1, by, 2, 4);
    }

    // Tribal mask/face paint
    gfx.fillStyle(0xcc4444, 0.8);
    gfx.fillRect(x - 3, headY - 2, 2, 3); // Left eye marking
    gfx.fillRect(x + 1, headY - 2, 2, 3); // Right eye marking

    // Staff with skull
    gfx.lineStyle(2.5, 0x654321, 1);
    gfx.beginPath();
    gfx.moveTo(x + 8, shoulderY);
    gfx.lineTo(x + 8, y + 14);
    gfx.strokePath();

    // Skull on staff top
    gfx.fillStyle(0xeeddcc, 1);
    gfx.fillCircle(x + 8, shoulderY - 6, 5);
    // Skull eye sockets
    gfx.fillStyle(0x222222, 1);
    gfx.fillCircle(x + 6, shoulderY - 7, 1.5);
    gfx.fillCircle(x + 10, shoulderY - 7, 1.5);
    // Skull teeth
    gfx.fillStyle(0xeeddcc, 1);
    gfx.fillRect(x + 6, shoulderY - 3, 4, 2);

    // Feathers on head
    gfx.lineStyle(1.5, 0xcc6633, 1);
    gfx.beginPath();
    gfx.moveTo(x, headY - 5);
    gfx.lineTo(x - 4, headY - 16);
    gfx.strokePath();
    gfx.lineStyle(1.5, 0x44aa44, 1);
    gfx.beginPath();
    gfx.moveTo(x, headY - 5);
    gfx.lineTo(x + 1, headY - 15);
    gfx.strokePath();
    gfx.lineStyle(1.5, 0xcc6633, 1);
    gfx.beginPath();
    gfx.moveTo(x, headY - 5);
    gfx.lineTo(x + 5, headY - 14);
    gfx.strokePath();

  }

  static drawEnemyArcher(
    gfx: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    color: number,
    walkPhase: number,
    isAttacking: boolean
  ): void {
    const scale = 0.95;
    const legOffset = isAttacking ? 0 : Math.sin(walkPhase) * 4 * scale;
    const armAngle = isAttacking ? -0.5 : Math.sin(walkPhase * 0.8) * 0.3;
    StickFigureGraphics.drawStickFigure(gfx, x, y, color, scale, armAngle, legOffset);

    const shoulderY = y - 16 * scale + 4 * scale;

    // Bow (facing left toward wall)
    gfx.lineStyle(2, 0x5a3d2b, 1);
    const bowX = x - 8 * scale;
    const bowCenterY = shoulderY + 2;
    gfx.beginPath();
    gfx.arc(bowX - 4, bowCenterY, 10 * scale, Math.PI * 0.6, Math.PI * 1.4, false);
    gfx.strokePath();

    // Bowstring
    gfx.lineStyle(1, 0xcccccc, 0.7);
    const stringPull = isAttacking ? 3 : 0;
    const topY = bowCenterY - Math.sin(Math.PI * 0.4) * 10 * scale;
    const botY = bowCenterY + Math.sin(Math.PI * 0.4) * 10 * scale;
    const topX = bowX - 4 - Math.cos(Math.PI * 0.4) * 10 * scale;
    const botX = topX;
    gfx.beginPath();
    gfx.moveTo(topX, topY);
    gfx.lineTo(bowX + stringPull, bowCenterY);
    gfx.lineTo(botX, botY);
    gfx.strokePath();

    // Arrow nocked when attacking
    if (isAttacking) {
      gfx.lineStyle(1.5, 0x8b4513, 1);
      gfx.beginPath();
      gfx.moveTo(bowX + stringPull, bowCenterY);
      gfx.lineTo(bowX - 14 * scale, bowCenterY);
      gfx.strokePath();
      // Arrowhead
      gfx.fillStyle(0xaaaaaa, 1);
      gfx.fillTriangle(
        bowX - 14 * scale, bowCenterY,
        bowX - 11 * scale, bowCenterY - 2,
        bowX - 11 * scale, bowCenterY + 2
      );
    }

    // Quiver on back
    gfx.fillStyle(0x5a3d2b, 0.7);
    gfx.fillRect(x + 6 * scale, shoulderY - 4, 3, 14 * scale);
    // Arrow tips poking out
    gfx.fillStyle(0xaaaaaa, 1);
    gfx.fillTriangle(x + 7 * scale, shoulderY - 6, x + 6 * scale, shoulderY - 4, x + 8 * scale, shoulderY - 4);
  }

  static drawEnemyLongbow(
    gfx: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    color: number,
    walkPhase: number,
    isAttacking: boolean
  ): void {
    const scale = 1.1;
    const legOffset = isAttacking ? 0 : Math.sin(walkPhase) * 3 * scale;
    const armAngle = isAttacking ? -0.4 : Math.sin(walkPhase * 0.7) * 0.2;
    StickFigureGraphics.drawStickFigure(gfx, x, y, color, scale, armAngle, legOffset, 2);

    const shoulderY = y - 16 * scale + 4 * scale;

    // LONGBOW (facing left toward wall) — much larger than normal bow
    gfx.lineStyle(2.5, 0x5c4033, 1);
    const bowX = x - 8 * scale;
    const bowCenterY = shoulderY + 2;
    gfx.beginPath();
    gfx.arc(bowX - 4, bowCenterY, 18 * scale, Math.PI * 0.55, Math.PI * 1.45, false);
    gfx.strokePath();

    // Bowstring
    gfx.lineStyle(1, 0xcccccc, 0.7);
    const stringPull = isAttacking ? 5 : 0;
    const topY = bowCenterY - Math.sin(Math.PI * 0.45) * 18 * scale;
    const botY = bowCenterY + Math.sin(Math.PI * 0.45) * 18 * scale;
    const topX = bowX - 4 - Math.cos(Math.PI * 0.45) * 18 * scale;
    const botX = topX;
    gfx.beginPath();
    gfx.moveTo(topX, topY);
    gfx.lineTo(bowX + stringPull, bowCenterY);
    gfx.lineTo(botX, botY);
    gfx.strokePath();

    // Arrow nocked when attacking
    if (isAttacking) {
      gfx.lineStyle(1.5, 0x8b4513, 1);
      gfx.beginPath();
      gfx.moveTo(bowX + stringPull, bowCenterY);
      gfx.lineTo(bowX - 18 * scale, bowCenterY);
      gfx.strokePath();
      // Arrowhead
      gfx.fillStyle(0xaaaaaa, 1);
      gfx.fillTriangle(
        bowX - 18 * scale, bowCenterY,
        bowX - 14 * scale, bowCenterY - 2.5,
        bowX - 14 * scale, bowCenterY + 2.5
      );
    }

    // Quiver on back (larger)
    gfx.fillStyle(0x5a3d2b, 0.7);
    gfx.fillRect(x + 6 * scale, shoulderY - 6, 4, 18 * scale);
    // Arrow tips poking out
    gfx.fillStyle(0xaaaaaa, 1);
    gfx.fillTriangle(x + 7 * scale, shoulderY - 8, x + 6 * scale, shoulderY - 6, x + 9 * scale, shoulderY - 6);
    gfx.fillTriangle(x + 9 * scale, shoulderY - 9, x + 8 * scale, shoulderY - 7, x + 10 * scale, shoulderY - 7);
  }

  static drawShieldbearer(
    gfx: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    color: number,
    walkPhase: number,
    isAttacking: boolean
  ): void {
    const scale = 1.1;
    const legOffset = isAttacking ? 0 : Math.sin(walkPhase) * 3 * scale;
    const armAngle = isAttacking ? -0.6 : Math.sin(walkPhase * 0.7) * 0.2;
    StickFigureGraphics.drawStickFigure(gfx, x, y, color, scale, armAngle, legOffset, 2.5);

    const shoulderY = y - 16 * scale + 4 * scale;

    // Large shield on left side (facing the wall/player)
    const shieldX = x - 10 * scale;
    const shieldY = shoulderY + 6 * scale;
    const shieldW = 10 * scale;
    const shieldH = 22 * scale;

    // Shield body
    gfx.fillStyle(0x8b7355, 1);
    gfx.fillRect(shieldX - shieldW / 2, shieldY - shieldH / 2, shieldW, shieldH);
    // Shield border
    gfx.lineStyle(2, 0x5a4a35, 1);
    gfx.strokeRect(shieldX - shieldW / 2, shieldY - shieldH / 2, shieldW, shieldH);
    // Shield boss (center circle)
    gfx.fillStyle(0xaaaaaa, 1);
    gfx.fillCircle(shieldX, shieldY, 3 * scale);
    // Shield cross decoration
    gfx.lineStyle(1.5, 0x5a4a35, 0.6);
    gfx.beginPath();
    gfx.moveTo(shieldX, shieldY - shieldH / 2 + 2);
    gfx.lineTo(shieldX, shieldY + shieldH / 2 - 2);
    gfx.moveTo(shieldX - shieldW / 2 + 2, shieldY);
    gfx.lineTo(shieldX + shieldW / 2 - 2, shieldY);
    gfx.strokePath();

    // Club in right hand
    const clubAngle = isAttacking ? -Math.PI / 3 : Math.PI / 5;
    const clubLen = 14 * scale;
    gfx.lineStyle(3, 0x654321, 1);
    gfx.beginPath();
    gfx.moveTo(x + 8 * scale, shoulderY);
    gfx.lineTo(x + 8 * scale + Math.cos(clubAngle) * clubLen, shoulderY + Math.sin(clubAngle) * clubLen);
    gfx.strokePath();
    // Club head
    gfx.fillStyle(0x5a3d2b, 1);
    gfx.fillCircle(
      x + 8 * scale + Math.cos(clubAngle) * clubLen,
      shoulderY + Math.sin(clubAngle) * clubLen,
      4 * scale
    );
  }

  // ---- SWARM ENEMY TYPES ----

  static drawSwarmling(
    gfx: Phaser.GameObjects.Graphics,
    x: number, y: number, color: number,
    walkPhase: number, isAttacking: boolean
  ): void {
    // Tiny, fast stickman — 0.6 scale, thin lines
    const scale = 0.6;
    const legOffset = isAttacking ? 0 : Math.sin(walkPhase * 2) * 5 * scale;
    const armAngle = isAttacking ? -0.4 : Math.sin(walkPhase * 1.5) * 0.5;
    StickFigureGraphics.drawStickFigure(gfx, x, y, color, scale, armAngle, legOffset, 1.5);
  }

  static drawSplitter(
    gfx: Phaser.GameObjects.Graphics,
    x: number, y: number, color: number,
    walkPhase: number, isAttacking: boolean
  ): void {
    const scale = 1.15;
    const legOffset = isAttacking ? 0 : Math.sin(walkPhase) * 4 * scale;
    const armAngle = isAttacking ? -0.5 : Math.sin(walkPhase * 0.8) * 0.3;
    StickFigureGraphics.drawStickFigure(gfx, x, y, color, scale, armAngle, legOffset, 2.5);

    const shoulderY = y - 16 * scale + 4 * scale;

    // Two small stick figures on back (the split children)
    const miniScale = 0.35;
    const backX = x + 6 * scale;
    StickFigureGraphics.drawStickFigure(gfx, backX, shoulderY + 2, color, miniScale, 0, 0, 1);
    StickFigureGraphics.drawStickFigure(gfx, backX + 5, shoulderY + 4, color, miniScale, 0, 0, 1);

    // Club weapon
    const clubAngle = isAttacking ? -Math.PI / 3 : Math.PI / 5;
    const clubLen = 14 * scale;
    gfx.lineStyle(3, 0x654321, 1);
    gfx.beginPath();
    gfx.moveTo(x - 8 * scale, shoulderY);
    gfx.lineTo(x - 8 * scale + Math.cos(clubAngle) * clubLen, shoulderY + Math.sin(clubAngle) * clubLen);
    gfx.strokePath();
    gfx.fillStyle(0x5a3d2b, 1);
    gfx.fillCircle(x - 8 * scale + Math.cos(clubAngle) * clubLen, shoulderY + Math.sin(clubAngle) * clubLen, 4 * scale);
  }

  static drawNestCarrier(
    gfx: Phaser.GameObjects.Graphics,
    x: number, y: number, color: number,
    walkPhase: number, isAttacking: boolean
  ): void {
    const scale = 1.05;
    const legOffset = isAttacking ? 0 : Math.sin(walkPhase * 0.7) * 3 * scale;
    const armAngle = isAttacking ? -0.3 : Math.sin(walkPhase * 0.5) * 0.2;
    StickFigureGraphics.drawStickFigure(gfx, x, y, color, scale, armAngle, legOffset, 2);

    const shoulderY = y - 16 * scale + 4 * scale;
    const headY = y - (16 * scale + 5 * scale * 2);

    // Woven basket on back
    const basketX = x + 7 * scale;
    const basketY = shoulderY + 2;
    const basketW = 12 * scale;
    const basketH = 14 * scale;
    gfx.fillStyle(0x8b6914, 0.8);
    gfx.fillRect(basketX - basketW / 2, basketY - basketH / 2, basketW, basketH);
    // Basket weave pattern
    gfx.lineStyle(1, 0x6b4f0a, 0.6);
    for (let dy = -basketH / 2 + 3; dy < basketH / 2; dy += 4) {
      gfx.beginPath();
      gfx.moveTo(basketX - basketW / 2, basketY + dy);
      gfx.lineTo(basketX + basketW / 2, basketY + dy);
      gfx.strokePath();
    }
    // Basket rim
    gfx.lineStyle(1.5, 0x6b4f0a, 1);
    gfx.strokeRect(basketX - basketW / 2, basketY - basketH / 2, basketW, basketH);

    // Tiny swarmling peeking out of basket
    const peekScale = 0.3;
    gfx.fillStyle(color, 1);
    gfx.fillCircle(basketX - 2, basketY - basketH / 2 - 2, 2.5 * peekScale);
    gfx.fillCircle(basketX + 3, basketY - basketH / 2 - 1, 2.5 * peekScale);
  }

  static drawEnemyTrebuchet(
    gfx: Phaser.GameObjects.Graphics,
    x: number, y: number, color: number,
    walkPhase: number, isAttacking: boolean
  ): void {
    // Enemy trebuchet faces LEFT (towards the wall).
    // walkPhase rotates the wheels.

    // Wheels
    gfx.lineStyle(2, 0x444444, 1);
    gfx.fillStyle(0x666666, 1);
    const wheelR = 8;
    const wheel1X = x - 15;
    const wheel2X = x + 15;
    const wheelY = y - wheelR;

    // Draw wheels with spokes rotating based on walkPhase
    for (const wx of [wheel1X, wheel2X]) {
      gfx.fillCircle(wx, wheelY, wheelR);
      gfx.strokeCircle(wx, wheelY, wheelR);
      // Spokes
      for (let i = 0; i < 4; i++) {
        const angle = walkPhase + (i * Math.PI) / 4;
        gfx.beginPath();
        gfx.moveTo(wx - Math.cos(angle) * wheelR, wheelY - Math.sin(angle) * wheelR);
        gfx.lineTo(wx + Math.cos(angle) * wheelR, wheelY + Math.sin(angle) * wheelR);
        gfx.strokePath();
      }
    }

    // Base chassis
    gfx.fillStyle(0x5c4033, 1); // Dark brown wood
    gfx.fillRect(x - 22, y - 16, 44, 6);
    gfx.lineStyle(1, 0x3e2723, 1);
    gfx.strokeRect(x - 22, y - 16, 44, 6);

    // A-Frame
    const pivotY = y - 45;
    gfx.lineStyle(4, 0x654321, 1);
    gfx.beginPath();
    // back leg
    gfx.moveTo(x + 10, y - 16);
    gfx.lineTo(x, pivotY);
    // front leg
    gfx.lineTo(x - 10, y - 16);
    gfx.strokePath();

    // Horizontal support
    gfx.lineStyle(3, 0x654321, 1);
    gfx.beginPath();
    gfx.moveTo(x - 6, y - 30);
    gfx.lineTo(x + 6, y - 30);
    gfx.strokePath();

    // Throwing Arm (pivot at x, pivotY)
    // When attacking, arm swings from back (right) to front (left).
    const armAngle = isAttacking ? -Math.PI / 4 : Math.PI / 6;

    const cwLen = 15;
    const slingLen = 30;

    // Counterweight end (back/right)
    const cwX = x + Math.cos(armAngle) * cwLen;
    const cwY = pivotY + Math.sin(armAngle) * cwLen;

    // Sling end (front/left)
    const slingEndX = x - Math.cos(armAngle) * slingLen;
    const slingEndY = pivotY - Math.sin(armAngle) * slingLen;

    // Draw arm
    gfx.lineStyle(4, 0x8b4513, 1);
    gfx.beginPath();
    gfx.moveTo(cwX, cwY);
    gfx.lineTo(slingEndX, slingEndY);
    gfx.strokePath();

    // Counterweight (heavy box hanging down)
    gfx.fillStyle(0x444444, 1);
    gfx.fillRect(cwX - 8, cwY, 16, 12);
    gfx.lineStyle(2, 0x222222, 1);
    gfx.strokeRect(cwX - 8, cwY, 16, 12);
    // Hinge for counterweight
    gfx.beginPath();
    gfx.moveTo(cwX, cwY);
    gfx.lineTo(cwX, cwY + 4);
    gfx.strokePath();

    // Sling & Rock
    if (!isAttacking) {
      // String hanging down
      gfx.lineStyle(1.5, 0xdddddd, 1);
      gfx.beginPath();
      gfx.moveTo(slingEndX, slingEndY);
      gfx.lineTo(slingEndX, slingEndY + 15);
      gfx.strokePath();
      // Rock
      gfx.fillStyle(0x777777, 1);
      gfx.fillCircle(slingEndX, slingEndY + 15, 4);
    } else {
      // String whipping forward
      gfx.lineStyle(1.5, 0xdddddd, 1);
      gfx.beginPath();
      gfx.moveTo(slingEndX, slingEndY);
      gfx.lineTo(slingEndX - 10, slingEndY - 5);
      gfx.strokePath();
    }

    // Pivot pin
    gfx.fillStyle(0x333333, 1);
    gfx.fillCircle(x, pivotY, 4);
  }

  // ---- NEW DEFENSIVE UNITS ----

  static drawClubSwinger(
    gfx: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    swingPhase: number,
    level: number
  ): void {
    const armAngle = Math.sin(swingPhase) * 0.8;
    StickFigureGraphics.drawStickFigure(gfx, x, y, 0x000000, 1, armAngle, 0);

    const shoulderY = y - 16 + 4;
    const clubAngle = Math.PI / 4 + Math.sin(swingPhase) * 1.0;
    const clubLen = 18;
    const clubEndX = x + Math.cos(clubAngle) * clubLen;
    const clubEndY = shoulderY + Math.sin(clubAngle) * clubLen;

    // Club handle
    gfx.lineStyle(3, 0x654321, 1);
    gfx.beginPath();
    gfx.moveTo(x + 5, shoulderY);
    gfx.lineTo(clubEndX, clubEndY);
    gfx.strokePath();

    // Club head (thick end)
    gfx.fillStyle(0x5a3d2b, 1);
    gfx.fillCircle(clubEndX, clubEndY, 6);

    // Level indicator
    if (level > 1) {
      gfx.fillStyle(0xffd700, 1);
      for (let i = 0; i < Math.min(level - 1, 5); i++) {
        gfx.fillCircle(x - 8 + i * 4, y + 16, 1.5);
      }
    }
  }

  static drawSlingThrower(
    gfx: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    throwPhase: number,
    level: number
  ): void {
    const armAngle = throwPhase > 0 ? -0.6 + throwPhase * 0.8 : 0.4;
    StickFigureGraphics.drawStickFigure(gfx, x, y, 0x000000, 1, armAngle, 0);

    const shoulderY = y - 16 + 4;

    // Sling
    if (throwPhase <= 0) {
      // Sling at rest with stone
      gfx.lineStyle(1.5, 0x8b7355, 1);
      const slingX = x + 10;
      const slingY = shoulderY + 8;
      // Sling cord
      gfx.beginPath();
      gfx.moveTo(x + 5, shoulderY);
      gfx.lineTo(slingX - 3, slingY);
      gfx.moveTo(x + 5, shoulderY);
      gfx.lineTo(slingX + 3, slingY);
      gfx.strokePath();
      // Pouch
      gfx.lineStyle(2, 0x654321, 1);
      gfx.beginPath();
      gfx.arc(slingX, slingY, 4, 0, Math.PI, false);
      gfx.strokePath();
      // Stone in pouch
      gfx.fillStyle(0x777777, 1);
      gfx.fillCircle(slingX, slingY + 2, 3);
    } else {
      // Spinning animation
      const spinAngle = throwPhase * Math.PI * 3;
      const spinRadius = 12;
      const stoneX = x + Math.cos(spinAngle) * spinRadius;
      const stoneY = shoulderY + Math.sin(spinAngle) * spinRadius;
      gfx.lineStyle(1.5, 0x8b7355, 1);
      gfx.beginPath();
      gfx.moveTo(x + 5, shoulderY);
      gfx.lineTo(stoneX, stoneY);
      gfx.strokePath();
      gfx.fillStyle(0x777777, 1);
      gfx.fillCircle(stoneX, stoneY, 3);
    }

    // Level indicator
    if (level > 1) {
      gfx.fillStyle(0xffd700, 1);
      for (let i = 0; i < Math.min(level - 1, 5); i++) {
        gfx.fillCircle(x - 8 + i * 4, y + 16, 1.5);
      }
    }
  }

  static drawSlingStone(gfx: Phaser.GameObjects.Graphics, x: number, y: number): void {
    // Motion blur effect
    gfx.fillStyle(0x888888, 0.3);
    gfx.fillEllipse(x - 4, y, 8, 4);
    // Stone
    gfx.fillStyle(0x666666, 1);
    gfx.fillCircle(x, y, 4);
    // Highlight
    gfx.fillStyle(0x999999, 0.6);
    gfx.fillCircle(x - 1, y - 1, 1.5);
  }

  static drawRockThrower(
    gfx: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    throwPhase: number,
    level: number
  ): void {
    const armAngle = throwPhase > 0 ? -0.8 + throwPhase : 0.3;
    StickFigureGraphics.drawStickFigure(gfx, x, y, 0x000000, 1, armAngle, 0);

    // Rock in hand
    if (throwPhase <= 0) {
      const shoulderY = y - 16 + 4;
      const rockX = x + Math.cos(Math.PI / 4 + 0.3) * 10;
      const rockY = shoulderY + Math.sin(Math.PI / 4 + 0.3) * 10;
      gfx.fillStyle(0x777777, 1);
      gfx.fillCircle(rockX, rockY, 3);
    }

    // Level indicator
    if (level > 1) {
      gfx.fillStyle(0xffd700, 1);
      for (let i = 0; i < Math.min(level - 1, 5); i++) {
        gfx.fillCircle(x - 8 + i * 4, y + 16, 1.5);
      }
    }
  }

  static drawSpearThrower(
    gfx: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    throwPhase: number,
    level: number
  ): void {
    const armAngle = throwPhase > 0 ? -1.0 + throwPhase : 0.2;
    StickFigureGraphics.drawStickFigure(gfx, x, y, 0x000000, 1, armAngle, 0);

    // Spear in hand
    if (throwPhase <= 0) {
      const shoulderY = y - 16 + 4;
      gfx.lineStyle(2, 0x8b4513, 1);
      gfx.beginPath();
      gfx.moveTo(x + 5, shoulderY - 5);
      gfx.lineTo(x + 20, shoulderY + 5);
      gfx.strokePath();
      // Spear tip
      gfx.fillStyle(0xaaaaaa, 1);
      gfx.fillTriangle(x + 20, shoulderY + 5, x + 23, shoulderY + 3, x + 21, shoulderY + 8);
    }

    if (level > 1) {
      gfx.fillStyle(0xffd700, 1);
      for (let i = 0; i < Math.min(level - 1, 5); i++) {
        gfx.fillCircle(x - 8 + i * 4, y + 16, 1.5);
      }
    }
  }

  static drawBuilder(
    gfx: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    hammerPhase: number,
    level: number
  ): void {
    const armAngle = Math.sin(hammerPhase) * 0.5;
    StickFigureGraphics.drawStickFigure(gfx, x, y, 0x000000, 1, armAngle, 0);

    // Hammer
    const shoulderY = y - 16 + 4;
    const hammerAngle = Math.sin(hammerPhase) * 0.6;
    const hx = x + Math.cos(Math.PI / 4 + hammerAngle) * 14;
    const hy = shoulderY + Math.sin(Math.PI / 4 + hammerAngle) * 14;
    gfx.lineStyle(2, 0x654321, 1);
    gfx.beginPath();
    gfx.moveTo(x + 5, shoulderY);
    gfx.lineTo(hx, hy);
    gfx.strokePath();
    gfx.fillStyle(0x888888, 1);
    gfx.fillRect(hx - 3, hy - 2, 6, 4);

    if (level > 1) {
      gfx.fillStyle(0xffd700, 1);
      for (let i = 0; i < Math.min(level - 1, 5); i++) {
        gfx.fillCircle(x - 8 + i * 4, y + 16, 1.5);
      }
    }
  }

  static drawCrowTrainer(
    gfx: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    level: number
  ): void {
    // Stickman with perch
    StickFigureGraphics.drawStickFigure(gfx, x, y, 0x000000, 1, -0.3, 0);

    // Perch
    const shoulderY = y - 16 + 4;
    gfx.lineStyle(2, 0x654321, 1);
    gfx.beginPath();
    gfx.moveTo(x - 15, y);
    gfx.lineTo(x - 15, shoulderY);
    gfx.lineTo(x - 5, shoulderY);
    gfx.strokePath();

    // Crow on perch (if not firing ideally, but just static for now)
    gfx.fillStyle(0x000000, 1);
    gfx.fillCircle(x - 10, shoulderY - 3, 3);
    gfx.fillTriangle(x - 10, shoulderY - 3, x - 13, shoulderY - 4, x - 10, shoulderY - 6);

    // Level
    if (level > 1) {
      gfx.fillStyle(0xffd700, 1);
      for (let i = 0; i < Math.min(level - 1, 5); i++) {
        gfx.fillCircle(x - 6 + i * 3, y + 8, 1.5);
      }
    }
  }

  static drawTrebuchet(
    gfx: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    level: number
  ): void {
    // Wooden frame
    gfx.lineStyle(3, 0x654321, 1);
    gfx.beginPath();
    gfx.moveTo(x - 10, y);
    gfx.lineTo(x, y - 20);
    gfx.lineTo(x + 10, y);
    gfx.strokePath();

    // Arm
    gfx.lineStyle(4, 0x8b4513, 1);
    gfx.beginPath();
    gfx.moveTo(x - 15, y - 5);
    gfx.lineTo(x + 10, y - 25);
    gfx.strokePath();

    // Counterweight
    gfx.fillStyle(0x555555, 1);
    gfx.fillRect(x - 18, y - 5, 8, 8);

    // Level
    if (level > 1) {
      gfx.fillStyle(0xffd700, 1);
      for (let i = 0; i < Math.min(level - 1, 5); i++) {
        gfx.fillCircle(x - 6 + i * 3, y + 8, 1.5);
      }
    }
  }

  static drawBeehiveThrower(
    gfx: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    level: number
  ): void {
    StickFigureGraphics.drawStickFigure(gfx, x, y, 0x000000, 1, -0.5, 0);

    // Holding Hive
    const shoulderY = y - 16 + 4;
    const hiveX = x + 8;
    const hiveY = shoulderY - 5;

    gfx.fillStyle(0xffaa00, 1);
    gfx.fillCircle(hiveX, hiveY, 6);
    // Stripes
    gfx.lineStyle(1, 0x664400, 0.5);
    gfx.strokeCircle(hiveX, hiveY, 6);
    gfx.beginPath();
    gfx.moveTo(hiveX - 5, hiveY - 2);
    gfx.lineTo(hiveX + 5, hiveY - 2);
    gfx.moveTo(hiveX - 5, hiveY + 2);
    gfx.lineTo(hiveX + 5, hiveY + 2);
    gfx.strokePath();

    if (level > 1) {
      gfx.fillStyle(0xffd700, 1);
      for (let i = 0; i < Math.min(level - 1, 5); i++) {
        gfx.fillCircle(x - 6 + i * 3, y + 8, 1.5);
      }
    }
  }

  static drawArcher(
    gfx: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    drawPhase: number,
    level: number
  ): void {
    const armAngle = drawPhase > 0 ? -0.6 + drawPhase * 0.4 : 0.2;
    StickFigureGraphics.drawStickFigure(gfx, x, y, 0x000000, 1, armAngle, 0);

    const shoulderY = y - 16 + 4;

    // Bow
    gfx.lineStyle(2, 0x8b5a2b, 1);
    const bowX = x + 8;
    const bowCenterY = shoulderY + 2;
    gfx.beginPath();
    gfx.arc(bowX + 4, bowCenterY, 12, -Math.PI * 0.4, Math.PI * 0.4, false);
    gfx.strokePath();

    // Bowstring
    gfx.lineStyle(1, 0xcccccc, 0.8);
    const stringPull = drawPhase > 0 ? drawPhase * 4 : 0;
    const topY = bowCenterY - Math.sin(Math.PI * 0.4) * 12;
    const botY = bowCenterY + Math.sin(Math.PI * 0.4) * 12;
    const topX = bowX + 4 + Math.cos(Math.PI * 0.4) * 12;
    const botX = topX;
    gfx.beginPath();
    gfx.moveTo(topX, topY);
    gfx.lineTo(bowX - stringPull, bowCenterY);
    gfx.lineTo(botX, botY);
    gfx.strokePath();

    // Arrow nocked if drawing
    if (drawPhase > 0) {
      gfx.lineStyle(1.5, 0x8b4513, 1);
      gfx.beginPath();
      gfx.moveTo(bowX - stringPull, bowCenterY);
      gfx.lineTo(bowX + 14, bowCenterY);
      gfx.strokePath();
      // Arrowhead
      gfx.fillStyle(0xaaaaaa, 1);
      gfx.fillTriangle(bowX + 14, bowCenterY, bowX + 11, bowCenterY - 2, bowX + 11, bowCenterY + 2);
    }

    // Quiver on back
    gfx.fillStyle(0x654321, 0.8);
    gfx.fillRect(x - 10, shoulderY - 4, 4, 16);
    // Arrow tips poking out
    gfx.fillStyle(0xaaaaaa, 1);
    gfx.fillTriangle(x - 9, shoulderY - 6, x - 10, shoulderY - 4, x - 8, shoulderY - 4);
    gfx.fillTriangle(x - 7, shoulderY - 7, x - 8, shoulderY - 5, x - 6, shoulderY - 5);

    // Level indicator (green for poison)
    if (level > 1) {
      gfx.fillStyle(0x44cc44, 1);
      for (let i = 0; i < Math.min(level - 1, 5); i++) {
        gfx.fillCircle(x - 8 + i * 4, y + 16, 1.5);
      }
    }
  }

  static drawArrow(gfx: Phaser.GameObjects.Graphics, x: number, y: number, angle: number, poison: boolean = false, fire: boolean = false): void {
    const len = 16;
    // Shaft — orange if fire, brown otherwise
    const shaftColor = fire ? 0xff6600 : 0x8b4513;
    gfx.lineStyle(1.5, shaftColor, 1);
    gfx.beginPath();
    gfx.moveTo(x - Math.cos(angle) * len / 2, y - Math.sin(angle) * len / 2);
    gfx.lineTo(x + Math.cos(angle) * len / 2, y + Math.sin(angle) * len / 2);
    gfx.strokePath();
    // Arrowhead — fire: orange-red, poison: green, default: grey
    const tipX = x + Math.cos(angle) * len / 2;
    const tipY = y + Math.sin(angle) * len / 2;
    const perpAngle = angle + Math.PI / 2;
    const tipColor = fire ? 0xff4400 : (poison ? 0x44cc44 : 0xaaaaaa);
    gfx.fillStyle(tipColor, 1);
    gfx.fillTriangle(
      tipX + Math.cos(angle) * 4, tipY + Math.sin(angle) * 4,
      tipX + Math.cos(perpAngle) * 2, tipY + Math.sin(perpAngle) * 2,
      tipX - Math.cos(perpAngle) * 2, tipY - Math.sin(perpAngle) * 2
    );
    // Fletching
    const backX = x - Math.cos(angle) * len / 2;
    const backY = y - Math.sin(angle) * len / 2;
    const fletchColor = fire ? 0xff8800 : (poison ? 0x44cc44 : 0x884422);
    gfx.lineStyle(1, fletchColor, 0.8);
    gfx.beginPath();
    gfx.moveTo(backX, backY);
    gfx.lineTo(backX + Math.cos(perpAngle) * 3, backY + Math.sin(perpAngle) * 3);
    gfx.moveTo(backX, backY);
    gfx.lineTo(backX - Math.cos(perpAngle) * 3, backY - Math.sin(perpAngle) * 3);
    gfx.strokePath();
    // Fire glow effect — flickering flame around arrowhead
    if (fire) {
      const flicker = Math.random() * 3;
      gfx.fillStyle(0xffaa00, 0.5);
      gfx.fillCircle(tipX + Math.cos(angle) * 2, tipY + Math.sin(angle) * 2, 3 + flicker);
      gfx.fillStyle(0xff4400, 0.3);
      gfx.fillCircle(tipX, tipY, 2 + flicker);
    }
  }

  static drawRock(gfx: Phaser.GameObjects.Graphics, x: number, y: number): void {
    // Shadow
    gfx.fillStyle(0x444444, 0.3);
    gfx.fillCircle(x + 1, y + 2, 7);
    // Rock body
    gfx.fillStyle(0x666666, 1);
    gfx.fillCircle(x, y, 6);
    // Highlight
    gfx.fillStyle(0x999999, 0.6);
    gfx.fillCircle(x - 2, y - 2, 3);
    // Outline
    gfx.lineStyle(1.5, 0x444444, 1);
    gfx.strokeCircle(x, y, 6);
  }

  static drawSpear(gfx: Phaser.GameObjects.Graphics, x: number, y: number, angle: number): void {
    const len = 22;
    // Shaft
    gfx.lineStyle(3, 0x8b4513, 1);
    gfx.beginPath();
    gfx.moveTo(x - Math.cos(angle) * len / 2, y - Math.sin(angle) * len / 2);
    gfx.lineTo(x + Math.cos(angle) * len / 2, y + Math.sin(angle) * len / 2);
    gfx.strokePath();
    // Spearhead
    const tipX = x + Math.cos(angle) * len / 2;
    const tipY = y + Math.sin(angle) * len / 2;
    gfx.fillStyle(0xcccccc, 1);
    gfx.fillCircle(tipX, tipY, 3);
    gfx.lineStyle(1, 0x888888, 1);
    gfx.strokeCircle(tipX, tipY, 3);
  }



  static drawCrow(gfx: Phaser.GameObjects.Graphics, x: number, y: number): void {
    gfx.fillStyle(0x000000, 1);
    // Bird shape
    gfx.beginPath();
    gfx.moveTo(x + 4, y);
    gfx.lineTo(x - 2, y - 3);
    gfx.lineTo(x - 2, y + 3);
    gfx.fillPath();
    // Wings
    gfx.beginPath();
    gfx.moveTo(x, y);
    gfx.lineTo(x - 2, y - 8);
    gfx.strokePath();
    gfx.beginPath();
    gfx.moveTo(x, y);
    gfx.lineTo(x - 2, y + 8);
    gfx.strokePath();
  }

  static drawHeavyRock(gfx: Phaser.GameObjects.Graphics, x: number, y: number): void {
    gfx.fillStyle(0x555555, 1);
    gfx.fillCircle(x, y, 10);
    gfx.fillStyle(0x333333, 1);
    gfx.fillCircle(x - 3, y - 2, 3);
  }

  static drawBeehive(gfx: Phaser.GameObjects.Graphics, x: number, y: number): void {
    gfx.fillStyle(0xffaa00, 1);
    gfx.fillCircle(x, y, 5);
    // Stripes
    gfx.lineStyle(1, 0x664400, 1);
    gfx.beginPath();
    gfx.moveTo(x - 4, y - 2);
    gfx.lineTo(x + 4, y - 2);
    gfx.moveTo(x - 4, y + 2);
    gfx.lineTo(x + 4, y + 2);
    gfx.strokePath();
  }

  static drawFirefly(gfx: Phaser.GameObjects.Graphics, x: number, y: number): void {
    // Glowing firefly body
    const flicker = Math.random() * 2;
    gfx.fillStyle(0xffcc00, 0.4);
    gfx.fillCircle(x, y, 6 + flicker);
    gfx.fillStyle(0xff8800, 0.8);
    gfx.fillCircle(x, y, 3);
    gfx.fillStyle(0xffff88, 1);
    gfx.fillCircle(x, y, 1.5);
  }

  static drawCrossbowStation(
    gfx: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    level: number
  ): void {
    // Tripod stand
    gfx.lineStyle(2.5, 0x654321, 1);
    gfx.beginPath();
    gfx.moveTo(x - 8, y);
    gfx.lineTo(x, y - 14);
    gfx.lineTo(x + 8, y);
    gfx.strokePath();

    // Crossbow body (horizontal)
    gfx.lineStyle(3, 0x5a3d2b, 1);
    gfx.beginPath();
    gfx.moveTo(x - 4, y - 14);
    gfx.lineTo(x + 16, y - 14);
    gfx.strokePath();

    // Bow limbs
    gfx.lineStyle(2, 0x4a3520, 1);
    gfx.beginPath();
    gfx.moveTo(x - 4, y - 14);
    gfx.lineTo(x - 10, y - 22);
    gfx.moveTo(x - 4, y - 14);
    gfx.lineTo(x - 10, y - 6);
    gfx.strokePath();

    // String
    gfx.lineStyle(1, 0xcccccc, 0.7);
    gfx.beginPath();
    gfx.moveTo(x - 10, y - 22);
    gfx.lineTo(x + 2, y - 14);
    gfx.lineTo(x - 10, y - 6);
    gfx.strokePath();

    // Bolt loaded
    gfx.lineStyle(1.5, 0x8b4513, 1);
    gfx.beginPath();
    gfx.moveTo(x + 2, y - 14);
    gfx.lineTo(x + 18, y - 14);
    gfx.strokePath();
    // Bolt tip
    gfx.fillStyle(0xaaaaaa, 1);
    gfx.fillTriangle(x + 18, y - 14, x + 15, y - 16, x + 15, y - 12);

    if (level > 1) {
      gfx.fillStyle(0x4488ff, 1);
      for (let i = 0; i < Math.min(level - 1, 5); i++) {
        gfx.fillCircle(x - 6 + i * 3, y + 8, 1.5);
      }
    }
  }

  static drawFireflyNest(
    gfx: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    level: number
  ): void {
    // Nest base — woven basket shape
    gfx.fillStyle(0x8b6914, 1);
    gfx.fillEllipse(x, y - 4, 18, 14);
    // Weave pattern
    gfx.lineStyle(1, 0x654321, 0.5);
    gfx.beginPath();
    gfx.moveTo(x - 8, y - 6);
    gfx.lineTo(x + 8, y - 6);
    gfx.moveTo(x - 7, y - 2);
    gfx.lineTo(x + 7, y - 2);
    gfx.strokePath();

    // Glowing fireflies hovering above
    gfx.fillStyle(0xffcc00, 0.6);
    gfx.fillCircle(x - 4, y - 14, 3);
    gfx.fillCircle(x + 5, y - 16, 2.5);
    gfx.fillCircle(x + 1, y - 18, 2);
    // Inner glow
    gfx.fillStyle(0xffff88, 1);
    gfx.fillCircle(x - 4, y - 14, 1);
    gfx.fillCircle(x + 5, y - 16, 1);

    // Stick support
    gfx.lineStyle(2, 0x654321, 1);
    gfx.beginPath();
    gfx.moveTo(x, y - 4);
    gfx.lineTo(x, y + 6);
    gfx.strokePath();

    if (level > 1) {
      gfx.fillStyle(0xff8800, 1);
      for (let i = 0; i < Math.min(level - 1, 5); i++) {
        gfx.fillCircle(x - 6 + i * 3, y + 8, 1.5);
      }
    }
  }

  static drawDruid(
    gfx: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    level: number
  ): void {
    // Stick figure in green robes
    StickFigureGraphics.drawStickFigure(gfx, x, y, 0x2d6b2d, 1, 0.1, 0);

    const shoulderY = y - 16 + 4;
    const headY = y - 16 - 10;

    // Wooden staff with vine wrapping
    gfx.lineStyle(2.5, 0x654321, 1);
    gfx.beginPath();
    gfx.moveTo(x + 8, shoulderY - 8);
    gfx.lineTo(x + 8, y + 14);
    gfx.strokePath();

    // Vine on staff
    gfx.lineStyle(1, 0x44aa44, 0.7);
    for (let i = 0; i < 4; i++) {
      const vy = shoulderY - 4 + i * 5;
      gfx.beginPath();
      gfx.moveTo(x + 6, vy);
      gfx.lineTo(x + 10, vy + 3);
      gfx.strokePath();
    }

    // Leaf crown
    gfx.fillStyle(0x44aa44, 0.8);
    gfx.fillTriangle(x - 4, headY - 5, x - 6, headY - 12, x - 1, headY - 9);
    gfx.fillTriangle(x, headY - 5, x, headY - 13, x + 4, headY - 8);
    gfx.fillTriangle(x + 3, headY - 5, x + 6, headY - 11, x + 6, headY - 7);

    if (level > 1) {
      gfx.fillStyle(0x44cc44, 1);
      for (let i = 0; i < Math.min(level - 1, 5); i++) {
        gfx.fillCircle(x - 6 + i * 3, y + 8, 1.5);
      }
    }
  }

  static drawLightningWizard(
    gfx: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    level: number
  ): void {
    // Stick figure in purple/blue robes
    StickFigureGraphics.drawStickFigure(gfx, x, y, 0x4455aa, 1, -0.3, 0);

    const shoulderY = y - 16 + 4;
    const headY = y - 16 - 10;

    // Wizard staff with crystal
    gfx.lineStyle(2.5, 0x555555, 1);
    gfx.beginPath();
    gfx.moveTo(x - 10, shoulderY - 6);
    gfx.lineTo(x - 10, y + 14);
    gfx.strokePath();

    // Crystal on staff top
    gfx.fillStyle(0x88ccff, 1);
    gfx.fillTriangle(x - 10, shoulderY - 14, x - 13, shoulderY - 6, x - 7, shoulderY - 6);
    // Crystal glow
    gfx.fillStyle(0xaaddff, 0.4);
    gfx.fillCircle(x - 10, shoulderY - 9, 5);

    // Pointed hat
    gfx.fillStyle(0x3344aa, 0.9);
    gfx.fillTriangle(x - 5, headY - 2, x, headY - 16, x + 5, headY - 2);

    // Tiny lightning bolts around hands
    gfx.lineStyle(1, 0xffff44, 0.6);
    gfx.beginPath();
    gfx.moveTo(x + 6, shoulderY + 2);
    gfx.lineTo(x + 9, shoulderY + 5);
    gfx.lineTo(x + 7, shoulderY + 7);
    gfx.lineTo(x + 10, shoulderY + 10);
    gfx.strokePath();

    if (level > 1) {
      gfx.fillStyle(0xffff44, 1);
      for (let i = 0; i < Math.min(level - 1, 5); i++) {
        gfx.fillCircle(x - 6 + i * 3, y + 8, 1.5);
      }
    }
  }

  static drawWall(
    gfx: Phaser.GameObjects.Graphics,
    x: number,
    yStart: number,
    yEnd: number,
    width: number,
    hpPercent: number
  ): void {
    // Wall color based on HP
    const r = Math.round(0x8b + (0xcc - 0x8b) * (1 - hpPercent));
    const g = Math.round(0x8b * hpPercent);
    const b = Math.round(0x8b * hpPercent);
    const color = (r << 16) | (g << 8) | b;

    gfx.fillStyle(color, 1);
    gfx.fillRect(x - width / 2, yStart, width, yEnd - yStart);

    // Brick pattern
    gfx.lineStyle(1, 0x666666, 0.3);
    const brickH = 12;
    for (let by = yStart; by < yEnd; by += brickH) {
      gfx.beginPath();
      gfx.moveTo(x - width / 2, by);
      gfx.lineTo(x + width / 2, by);
      gfx.strokePath();

      const offset = (Math.floor((by - yStart) / brickH) % 2) * (width / 2);
      gfx.beginPath();
      gfx.moveTo(x - width / 2 + offset, by);
      gfx.lineTo(x - width / 2 + offset, by + brickH);
      gfx.strokePath();
    }

    // Battlements on top
    gfx.fillStyle(0x9e9e9e, 1);
    const merlonW = 8;
    const merlonH = 6;
    for (let mx = x - width / 2 - 4; mx <= x + width / 2 + 4; mx += merlonW * 2) {
      gfx.fillRect(mx, yStart - merlonH, merlonW, merlonH);
    }

    // Cracks when damaged
    if (hpPercent < 0.6) {
      gfx.lineStyle(1, 0x444444, 0.6);
      const crackCount = Math.floor((1 - hpPercent) * 8);
      for (let i = 0; i < crackCount; i++) {
        const cy = yStart + ((i * 67 + 23) % (yEnd - yStart));
        const cx = x - width / 4;
        gfx.beginPath();
        gfx.moveTo(cx, cy);
        gfx.lineTo(cx + 3, cy + 5);
        gfx.lineTo(cx - 2, cy + 9);
        gfx.strokePath();
      }
    }
  }

  /** Rolling boulder — large procedural stone with rotating crack pattern */
  static drawBoulder(
    gfx: Phaser.GameObjects.Graphics,
    x: number, y: number, color: number,
    rollPhase: number, isCrashed: boolean
  ): void {
    const r = 36; // radius — large stone
    const bx = x;
    const by = y - r; // center sits r pixels above anchor

    const isHit = color !== COLORS.enemy;

    // Main body
    const bodyColor = isHit ? 0xaa7755 : (isCrashed ? 0x4a4a4a : 0x666666);
    gfx.fillStyle(bodyColor, 1);
    gfx.fillCircle(bx, by, r);

    // Highlight (top-left)
    gfx.fillStyle(isHit ? 0xcc9977 : 0x888888, 0.45);
    gfx.fillCircle(bx - r * 0.28, by - r * 0.28, r * 0.38);

    // Dark rim
    gfx.lineStyle(3, isHit ? 0x884422 : 0x333333, 1);
    gfx.strokeCircle(bx, by, r);

    if (!isCrashed) {
      // Rolling cracks — rotate with rollPhase to show motion
      gfx.lineStyle(2, isHit ? 0x774422 : 0x444444, 0.85);
      const numCracks = 5;
      for (let i = 0; i < numCracks; i++) {
        const baseAngle = rollPhase + (i / numCracks) * Math.PI * 2;
        const innerR = r * 0.22;
        const outerR = r * (0.65 + (i % 2) * 0.2);
        const jitter = ((i * 7) % 5 - 2) * 0.15; // deterministic wobble
        gfx.beginPath();
        gfx.moveTo(bx + Math.cos(baseAngle) * innerR, by + Math.sin(baseAngle) * innerR);
        gfx.lineTo(
          bx + Math.cos(baseAngle + jitter) * outerR,
          by + Math.sin(baseAngle + jitter) * outerR
        );
        gfx.strokePath();
      }

      // Rolling marker dot — orbits to show spin direction
      const markerAngle = rollPhase;
      const markerR = r * 0.6;
      gfx.fillStyle(isHit ? 0x993311 : 0x333333, 1);
      gfx.fillCircle(
        bx + Math.cos(markerAngle) * markerR,
        by + Math.sin(markerAngle) * markerR,
        4
      );
    } else {
      // Crashed state — big fracture lines
      gfx.lineStyle(3, isHit ? 0x884422 : 0x222222, 1);
      // Horizontal fracture
      gfx.beginPath();
      gfx.moveTo(bx - r * 0.9, by + r * 0.15);
      gfx.lineTo(bx - r * 0.1, by - r * 0.05);
      gfx.lineTo(bx + r * 0.7, by + r * 0.2);
      gfx.strokePath();
      // Diagonal fracture
      gfx.beginPath();
      gfx.moveTo(bx - r * 0.3, by - r * 0.9);
      gfx.lineTo(bx + r * 0.1, by + r * 0.1);
      gfx.lineTo(bx + r * 0.5, by + r * 0.85);
      gfx.strokePath();
    }
  }

  // ---- Boomerang Thrower (friendly wall unit) ----

  static drawBoomerangThrower(
    gfx: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    throwPhase: number,  // 0 = idle/ready, 0→1 = mid-throw arc
    scale: number = 1
  ): void {
    const color = 0x000000; // Black stickman body

    // Arm angle: cocked back at 0, flung forward at peak throw
    const armAngle = throwPhase > 0 ? -0.8 + throwPhase * 1.4 : 0.3;
    const legOffset = Math.sin(throwPhase * Math.PI) * 4 * scale;
    StickFigureGraphics.drawStickFigure(gfx, x, y, color, scale, armAngle, legOffset);

    // Boomerang held in right hand (or in flight arc near it)
    const bodyLen = 16 * scale;
    const neckY = y - bodyLen;
    const shoulderY = neckY + 4 * scale;

    // Right arm tip position
    const armLen = 10 * scale;
    const rightArmAngle = Math.PI / 4 - armAngle;
    const boomX = x + Math.cos(rightArmAngle) * armLen;
    const boomY = shoulderY + Math.sin(rightArmAngle) * armLen;

    // Boomerang shape: two curved wings forming a crescent
    const boomAngle = throwPhase * Math.PI * 1.2; // spins during throw
    StickFigureGraphics.drawBoomerangShape(gfx, boomX, boomY, boomAngle, scale * 0.9, 0xc8860a);
  }

  /** Draws a boomerang crescent shape at (cx, cy) rotated by angle */
  static drawBoomerangShape(
    gfx: Phaser.GameObjects.Graphics,
    cx: number,
    cy: number,
    angle: number,
    scale: number = 1,
    color: number = 0xc8860a
  ): void {
    const len = 11 * scale;   // wing half-length
    const bend = 5 * scale;   // lateral bow of each wing

    gfx.lineStyle(2.5 * scale, color, 1);

    // Wing 1 (tip A → elbow → tip B): curved crescent
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);

    // Helper to rotate a point
    const rot = (px: number, py: number) => ({
      x: cx + px * cos - py * sin,
      y: cy + px * sin + py * cos,
    });

    const tipA = rot(-len, 0);
    const midA = rot(-len * 0.4, -bend);
    const elbow = rot(0, -bend * 0.3);
    const midB = rot(len * 0.4, -bend);
    const tipB = rot(len, 0);

    gfx.beginPath();
    gfx.moveTo(tipA.x, tipA.y);
    gfx.lineTo(midA.x, midA.y);
    gfx.lineTo(elbow.x, elbow.y);
    gfx.lineTo(midB.x, midB.y);
    gfx.lineTo(tipB.x, tipB.y);
    gfx.strokePath();

    // Center notch knob
    gfx.fillStyle(color, 1);
    gfx.fillCircle(elbow.x, elbow.y, 2 * scale);
    // Wing tips
    gfx.fillCircle(tipA.x, tipA.y, 1.5 * scale);
    gfx.fillCircle(tipB.x, tipB.y, 1.5 * scale);
  }

  /** Draw a flying boomerang projectile (used in Projectile.draw()) */
  static drawBoomerang(
    gfx: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    angle: number
  ): void {
    StickFigureGraphics.drawBoomerangShape(gfx, x, y, angle, 1.0, 0xd49a0c);
  }

  // ---- NEW ENEMY TYPES ----

  static drawSaboteur(
    gfx: Phaser.GameObjects.Graphics,
    x: number, y: number, color: number,
    walkPhase: number, isAttacking: boolean
  ): void {
    if (!isAttacking) {
      // Burrowed: draw a dirt mound traveling underground
      const wobble = Math.sin(walkPhase * 3) * 2;
      // Ground line
      gfx.fillStyle(0x8B6914, 0.9);
      gfx.fillEllipse(x, y + wobble, 24, 10);
      // Dirt particles on top
      gfx.fillStyle(0x6B4F0A, 0.8);
      gfx.fillCircle(x - 6, y - 4 + wobble, 3);
      gfx.fillCircle(x + 4, y - 5 + wobble, 2.5);
      gfx.fillCircle(x, y - 6 + wobble, 2);
      // Trailing dirt
      gfx.fillStyle(0x8B6914, 0.4);
      gfx.fillCircle(x + 14, y + 2, 2);
      gfx.fillCircle(x + 18, y + 1, 1.5);
    } else {
      // Emerged: stickman with pickaxe
      const scale = 0.95;
      const legOffset = Math.sin(walkPhase) * 3 * scale;
      const armAngle = -0.6;
      StickFigureGraphics.drawStickFigure(gfx, x, y, color, scale, armAngle, legOffset);

      const shoulderY = y - 16 * scale + 4 * scale;

      // Pickaxe
      gfx.lineStyle(2, 0x654321, 1);
      const pickAngle = -Math.PI / 3;
      const pickLen = 16 * scale;
      const pickEndX = x - 8 * scale + Math.cos(pickAngle) * pickLen;
      const pickEndY = shoulderY + Math.sin(pickAngle) * pickLen;
      gfx.beginPath();
      gfx.moveTo(x - 8 * scale, shoulderY);
      gfx.lineTo(pickEndX, pickEndY);
      gfx.strokePath();
      // Pick head
      gfx.lineStyle(3, 0x888888, 1);
      const headAngle = pickAngle + Math.PI / 2;
      gfx.beginPath();
      gfx.moveTo(pickEndX - Math.cos(headAngle) * 6, pickEndY - Math.sin(headAngle) * 6);
      gfx.lineTo(pickEndX + Math.cos(headAngle) * 6, pickEndY + Math.sin(headAngle) * 6);
      gfx.strokePath();
      // Miner's helmet
      const headY = y - (16 * scale + 5 * scale * 2);
      gfx.fillStyle(0xddaa00, 1);
      gfx.fillRect(x - 6 * scale, headY - 3, 12 * scale, 4);
    }
  }

  static drawWarDrummer(
    gfx: Phaser.GameObjects.Graphics,
    x: number, y: number, color: number,
    walkPhase: number, isAttacking: boolean
  ): void {
    const scale = 1.15;
    const legOffset = isAttacking ? 0 : Math.sin(walkPhase) * 3 * scale;
    const armAngle = isAttacking ? -0.3 : Math.sin(walkPhase * 2) * 0.5;
    StickFigureGraphics.drawStickFigure(gfx, x, y, color, scale, armAngle, legOffset, 2.5);

    const shoulderY = y - 16 * scale + 4 * scale;

    // Drum body (large cylinder worn in front)
    const drumX = x;
    const drumY = shoulderY + 10 * scale;
    const drumW = 14 * scale;
    const drumH = 12 * scale;
    gfx.fillStyle(0x8B4513, 1);
    gfx.fillRect(drumX - drumW / 2, drumY - drumH / 2, drumW, drumH);
    // Drum skin (top)
    gfx.fillStyle(0xD2B48C, 1);
    gfx.fillRect(drumX - drumW / 2, drumY - drumH / 2, drumW, 3);
    // Drum straps
    gfx.lineStyle(1.5, 0x5a3d2b, 0.8);
    gfx.beginPath();
    gfx.moveTo(drumX - drumW / 2, drumY - drumH / 2);
    gfx.lineTo(x - 3 * scale, shoulderY - 4 * scale);
    gfx.moveTo(drumX + drumW / 2, drumY - drumH / 2);
    gfx.lineTo(x + 3 * scale, shoulderY - 4 * scale);
    gfx.strokePath();
    // Drum sticks
    const stickAngle1 = Math.PI / 6 + armAngle * 0.5;
    const stickAngle2 = Math.PI / 6 - armAngle * 0.5;
    gfx.lineStyle(1.5, 0x654321, 1);
    gfx.beginPath();
    gfx.moveTo(x - 6 * scale, shoulderY);
    gfx.lineTo(x - 6 * scale + Math.cos(stickAngle1) * 12, shoulderY + Math.sin(stickAngle1) * 12);
    gfx.strokePath();
    gfx.beginPath();
    gfx.moveTo(x + 6 * scale, shoulderY);
    gfx.lineTo(x + 6 * scale + Math.cos(stickAngle2) * 12, shoulderY + Math.sin(stickAngle2) * 12);
    gfx.strokePath();
    // Stick tips
    gfx.fillStyle(0xeeeeee, 1);
    gfx.fillCircle(x - 6 * scale + Math.cos(stickAngle1) * 12, shoulderY + Math.sin(stickAngle1) * 12, 2);
    gfx.fillCircle(x + 6 * scale + Math.cos(stickAngle2) * 12, shoulderY + Math.sin(stickAngle2) * 12, 2);
  }

  static drawLootGoblin(
    gfx: Phaser.GameObjects.Graphics,
    x: number, y: number, color: number,
    walkPhase: number, isAttacking: boolean
  ): void {
    // Tiny, hunched thief with a sack
    const scale = 0.7;
    const legOffset = isAttacking ? 0 : Math.sin(walkPhase * 2.5) * 5 * scale;
    const armAngle = isAttacking ? 0.2 : Math.sin(walkPhase * 2) * 0.6;
    // Olive/dark green tint for goblin
    StickFigureGraphics.drawStickFigure(gfx, x, y, 0x556B2F, scale, armAngle, legOffset, 1.5);

    const shoulderY = y - 16 * scale + 4 * scale;
    const headY = y - (16 * scale + 5 * scale * 2);

    // Large sack on back
    gfx.fillStyle(0x8B7355, 0.9);
    gfx.fillCircle(x + 7 * scale, shoulderY + 2, 8 * scale);
    // Sack tie
    gfx.lineStyle(1.5, 0x5a4a35, 1);
    gfx.beginPath();
    gfx.moveTo(x + 7 * scale, shoulderY - 6 * scale);
    gfx.lineTo(x + 7 * scale, shoulderY - 8 * scale);
    gfx.strokePath();

    // Pointy ears
    gfx.fillStyle(0x556B2F, 1);
    gfx.fillTriangle(x - 5 * scale, headY - 2, x - 8 * scale, headY - 6, x - 3 * scale, headY - 4);
    gfx.fillTriangle(x + 5 * scale, headY - 2, x + 8 * scale, headY - 6, x + 3 * scale, headY - 4);

    // Gold coin peeking out of sack
    gfx.fillStyle(0xffd700, 1);
    gfx.fillCircle(x + 5 * scale, shoulderY - 3 * scale, 2);
    gfx.fillCircle(x + 9 * scale, shoulderY - 2 * scale, 1.5);
  }

  static drawSiegeTower(
    gfx: Phaser.GameObjects.Graphics,
    x: number, y: number, color: number,
    walkPhase: number, isAttacking: boolean
  ): void {
    // Massive wooden box on wheels
    const bodyW = 30;
    const bodyH = 50;

    // Wheels
    const wheelR = 6;
    const wheelY = y - wheelR;
    gfx.lineStyle(2, 0x444444, 1);
    gfx.fillStyle(0x666666, 1);
    for (const wx of [x - 12, x + 12]) {
      gfx.fillCircle(wx, wheelY, wheelR);
      gfx.strokeCircle(wx, wheelY, wheelR);
      // Spokes
      for (let i = 0; i < 4; i++) {
        const angle = walkPhase + (i * Math.PI) / 4;
        gfx.beginPath();
        gfx.moveTo(wx - Math.cos(angle) * wheelR, wheelY - Math.sin(angle) * wheelR);
        gfx.lineTo(wx + Math.cos(angle) * wheelR, wheelY + Math.sin(angle) * wheelR);
        gfx.strokePath();
      }
    }

    // Main body
    gfx.fillStyle(0x5c4033, 1);
    gfx.fillRect(x - bodyW / 2, y - bodyH, bodyW, bodyH - wheelR);
    gfx.lineStyle(2, 0x3e2723, 1);
    gfx.strokeRect(x - bodyW / 2, y - bodyH, bodyW, bodyH - wheelR);

    // Horizontal planks
    gfx.lineStyle(1, 0x4a3520, 0.6);
    for (let dy = 8; dy < bodyH - wheelR; dy += 8) {
      gfx.beginPath();
      gfx.moveTo(x - bodyW / 2, y - bodyH + dy);
      gfx.lineTo(x + bodyW / 2, y - bodyH + dy);
      gfx.strokePath();
    }

    // Battlement/crenellations at top
    const crenW = 6;
    const crenH = 5;
    gfx.fillStyle(0x5c4033, 1);
    for (let cx = x - bodyW / 2; cx < x + bodyW / 2; cx += crenW * 2) {
      gfx.fillRect(cx, y - bodyH - crenH, crenW, crenH);
    }

    // Small stick figure peeking from top
    const miniScale = 0.3;
    StickFigureGraphics.drawStickFigure(gfx, x, y - bodyH - crenH + 2, color, miniScale, 0, 0, 1);

    // Gate/door opening at front
    gfx.fillStyle(0x3e2723, 1);
    gfx.fillRect(x - bodyW / 2, y - 20, 6, 14);
  }
}
